import { useState, useEffect } from 'react';
import { format, addDays, subDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import StatisticsAPI, { CustomerVisit } from '../../../api/StatisticsAPI';
import ListContainer from '../../../components/ListContainer/ListContainer';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import ElementActions from "../../../components/ElementActions/ElementActions";
import IconButton from '../../../components/IconButton/IconButton';
import { Icon } from '../../../components/Icon/Icon';
import deleteIcon from "../../../assets/images/bin.svg";
import ConfirmationPopup from '../../../components/ConfirmationPopup/ConfirmationPopup';
import { useTranslation } from 'react-i18next';
import SecondaryButton from '../../../components/SecondaryButton/SecondaryButton';

function VisitList() {
  const { t } = useTranslation();
  const [visits, setVisits] = useState<CustomerVisit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const fetchVisits = async () => {
    const nextDay = endOfDay(selectedDate).toISOString();
    const prevDay = startOfDay(selectedDate).toISOString();
    setIsLoading(true);
    const response = await StatisticsAPI.getVisitorsList(prevDay, nextDay);
    setIsLoading(false);
    setVisits(response.data);
  };

  const removeVisit = async (id: number) => {
    setIsLoading(true);
    await StatisticsAPI.deleteVisit(id);
    const nextDay = endOfDay(selectedDate).toISOString();
    const prevDay = startOfDay(selectedDate).toISOString();
    const response = await StatisticsAPI.getVisitorsList(prevDay, nextDay);
    setIsLoading(false);
    setVisits(response.data);
  }

  useEffect(() => {
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleNextDayClick = () => {
    const nextDay = addDays(selectedDate, 1);
    setSelectedDate(nextDay);
  };

  const handlePrevDayClick = () => {
    const prevDay = subDays(selectedDate, 1);
    setSelectedDate(prevDay);
  };

  const toVisitElement = (visit: CustomerVisit) => (
    <div key={visit.id} className="hover:bg-darker-gold px-4 py-2 rounded-lg shadow-sm transition-colors duration-200 hover:none flex justify-between">
        <ElementActions actions={
          <IconButton>
            <ConfirmationPopup onConfirm={() => removeVisit(visit.id)} confirmText={"global.confirm"} title={`${t("global.remove")}: ${visit.name}`}>
              <Icon url={deleteIcon} cssStyles="w-5"/>
            </ConfirmationPopup>
          </IconButton>
        }>
          <p><span className="font-semibold">{visit.name}</span> - {visit.dateOfWedding} - {visit.region}</p>
        </ElementActions>
    </div>
  );

  return (
    <div>
      <SectionTitle text={`Visits for ${format(selectedDate, 'MMM d, yyyy')}`}/>
      <div className="space-x-5 flex items-center">
        <SecondaryButton text={t("global.previous")!} isDisabled={isLoading} fn={handlePrevDayClick} />
        <SecondaryButton text={t("global.next")!} isDisabled={isLoading} fn={handleNextDayClick} />
        <DatePicker selected={selectedDate} onChange={(date) => setSelectedDate(date as Date)} />
      </div>
      <p>{ t("stats.visits_x_day.total") }: { visits.length }</p>
      <ListContainer
        elements={visits}
        toElementFn={toVisitElement}
        isLoading={isLoading}
        loadingNumber={10}
        emptyListComponent={<><p>{t("stats.visits_x_day.empty")}.</p></>}
      />
    </div>
  );
}

export default VisitList;
